import React, { useEffect, useState } from "react";
import { ISeatDetail } from "../../../../models/trip";
import { useForm } from "antd/es/form/Form";
import { AutoComplete, Form, Input, Modal } from "antd";
import { useAppDispatch, useAppSelector } from "../../../../lib/hook";
import { onGetListCustomer } from "../../../../lib/features/customers/customers.thunk";
import { ICustomer } from "../../../../lib/features/customers/customers.constant";
import { removeVietnameseTones } from "../../../../function/removeVietnameaseTones";
import { FormProps } from "antd/lib";
import { addSeatApi } from "../../../../apis/seat.api";
import { ErrorPopup, SuccessPopup } from "../../../../utilities/swal";
import { customersSlice } from "../../../../lib/features/customers/customers.slice";

interface ModalAddSeatProps {
  visible: boolean;
  onClose: () => void;
  selectedSeats: ISeatDetail[];

  tripId: string;
  busId: string;
  onSuccess: (value: ISeatDetail[]) => void;
}

const ModalAddSeat: React.FC<ModalAddSeatProps> = (props) => {
  const { visible, onClose, selectedSeats, onSuccess, busId, tripId } = props;

  const [options, setOptions] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [form] = useForm();

  const dispatch = useAppDispatch();
  const { isGot, customers } = useAppSelector((s) => s?.customers);

  /** Gọi danh sách khách */
  const fetchCustomers = async () => {
    setLoading(true);
    await dispatch(onGetListCustomer());
    setLoading(false);
  };

  /** Reset form + load khách khi mở modal */
  useEffect(() => {
    if (visible) {
      if (!isGot) fetchCustomers();
      form.resetFields();

      // load options mặc định
      setOptions(convertOptions(customers));
    }
  }, [visible]);

  /** Cập nhật options khi customers thay đổi */
  useEffect(() => {
    setOptions(convertOptions(customers));
  }, [customers]);

  /** Convert customer → options cho AutoComplete */
  const convertOptions = (list: ICustomer[]) =>
    list.map((c) => ({
      value: c?._id,
      label: (
        <div className="flex justify-between items-center">
          <span className="text-lg font-medium">{c.shortName}</span>
          <span className="text-gray-400 text-sm">{c?.majorPhone}</span>
        </div>
      ),
      raw: c,
    }));

  /** Search theo short name */
  const handleSearch = (text: string) => {
    const keyword = removeVietnameseTones(text?.toLowerCase().trim());

    if (!keyword) {
      setOptions(convertOptions(customers));
      return;
    }

    const filtered = customers.filter((u) => {
      const shortName = removeVietnameseTones(u?.shortNameNoAccent ?? "");
      return shortName.includes(keyword);
    });

    setOptions(convertOptions(filtered));
  };

  /** Khi chọn 1 khách → đổ vào form */
  const handleSelect = (value: string, option: any) => {
    const user: ICustomer = option?.raw;

    form.setFieldsValue({
      fullName: user?.shortName,
      customerId: user?._id,
    });
  };
  const handleInputChange = (value: string) => {
    // Khi gõ bất kỳ text nào -> không còn là chọn từ danh sách
    form.setFieldsValue({
      fullName: value,
      customerId: undefined,
    });

    handleSearch(value);
  };
  const handleOnSubmit: FormProps<{
    customerId: string | undefined;
    fullName: string;
  }>["onFinish"] = async (values) => {
    setLoading(true);
    const payload = {
      customerId: values?.customerId ? values.customerId : undefined,
      seatIds: selectedSeats.map((s) => s._id),
      tripId: tripId,
      busId: busId,
      isNewCustomer: values?.customerId ? false : true,
      shortName: values?.fullName,
    };
    try {
      const rs = await addSeatApi(payload);
      if (rs && rs.success) {
        const { seats, newCustomer } = rs?.data;
        form.resetFields();
        if (newCustomer) {
          const action = customersSlice.actions.addCustomer(newCustomer);
          dispatch(action);
        }
        onSuccess(seats);
        onClose && onClose();
      } else {
        ErrorPopup("Thêm vé thất bại", rs?.message || "");
      }
    } catch (error: any) {
      ErrorPopup("Thêm vé thất bại", error?.message || "");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title="Thêm vé"
      open={visible}
      onCancel={onClose}
      onOk={() => {
        form.submit();
      }}
      confirmLoading={loading}
    >
      <Form onFinish={handleOnSubmit} form={form} layout="vertical">
        <Form.Item name="customerId" hidden>
          <Input />
        </Form.Item>

        <Form.Item
          label="Khách hàng"
          name="fullName"
          rules={[
            { required: true, message: "Vui lòng nhập hoặc chọn khách hàng!" },
          ]}
        >
          <AutoComplete
            options={options}
            onSearch={handleSearch} // filter
            onChange={handleInputChange} // gõ text
            onSelect={handleSelect} // click chọn
            placeholder="Nhập tên khách hàng..."
            allowClear
            style={{ width: "100%" }}
            size="large"
            id="fakeField"
          />
        </Form.Item>
      </Form>
      <div className="flex items-center gap-2 font-medium">
        <p>Số ghế : </p>{" "}
        <p className="text-blue-600">
          {selectedSeats?.map((s) => s.seatCode).join(", ")}
        </p>
      </div>
    </Modal>
  );
};

export default ModalAddSeat;
